import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Search, ShieldCheck, Zap, ArrowRight, CheckCircle2, KeyRound,
  AlertCircle, Loader2, ExternalLink,
} from "lucide-react";
import { TopBar } from "@/components/PageShell";
import ImageSlot from "@/components/ImageSlot";
import Intel360ServiceCard from "@/components/Intel360ServiceCard";
import { getLucideIcon } from "@/lib/lucideIcon";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";
const GOLD_LIGHT = "#D9BF82";

export default function Inteligencia360() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]); // do backend (dinâmico)
  const [openSlug, setOpenSlug] = useState(null);
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [s, p, o, c] = await Promise.all([
        api.get("/intel/status"),
        api.get("/intel/products", { params: { include_inactive: true } }),
        api.get("/intel/orders/mine"),
        api.get("/intel/categories").catch(() => ({ data: { items: [] } })),
      ]);
      setStatus(s.data);
      setProducts(p.data.items || []);
      setOrders(o.data.items || []);
      setCategories(c.data.items || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const oid = params.get("order");
    const cancelled = params.get("cancelled");
    if (oid) {
      toast.success("Pagamento em processamento — abra Meus Pedidos");
      params.delete("order"); setParams(params, { replace: true });
      const t = setTimeout(loadAll, 1500);
      return () => clearTimeout(t);
    }
    if (cancelled) {
      toast.info("Compra cancelada. Você pode tentar novamente.");
      params.delete("cancelled"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  // Index de categorias por slug para O(1) lookups
  const catBySlug = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.slug] = c;
    return m;
  }, [categories]);

  const getCatLabel = (slug) => catBySlug[slug]?.name || (slug || "Outros");

  // Vitrine · produtos em destaque (featured=true) OU explicitamente marcados como launch.
  // Fallback: se não houver featured, usa os 3 primeiros ativos (sort_order asc).
  const launchProducts = useMemo(() => {
    const featured = products.filter(p => p.featured && p.active);
    if (featured.length > 0) return featured;
    return products
      .filter(p => p.active)
      .sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100))
      .slice(0, 3);
  }, [products]);

  // Catálogo completo · agrupa por categoria (excluindo os da vitrine).
  const catalogByCategory = useMemo(() => {
    const featuredSlugs = new Set(launchProducts.map(p => p.slug));
    const groups = {};
    for (const p of products) {
      if (featuredSlugs.has(p.slug)) continue;
      const cat = p.category || "outros";
      (groups[cat] = groups[cat] || []).push(p);
    }
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return (a.sort_order || 100) - (b.sort_order || 100) || (a.name || "").localeCompare(b.name || "");
      });
    }
    // Ordem: seguir sort_order das categorias do backend; categorias sem cadastro vão no fim.
    const orderedSlugs = [
      ...categories.filter(c => groups[c.slug]).map(c => c.slug),
      ...Object.keys(groups).filter(s => !catBySlug[s]),
    ];
    return orderedSlugs.map(s => [s, groups[s] || []]).filter(([, list]) => list.length > 0);
  }, [products, launchProducts, categories, catBySlug]);

  const openBuy = (slug) => { setOpenSlug(slug); setIdentifier(""); };

  const submitBuy = async () => {
    if (!openSlug || !identifier.trim()) return;
    setBusy(true);
    try {
      const q = await api.post("/intel/orders/quote", {
        product_slug: openSlug, identifier: identifier.trim(),
      });
      const orderId = q.data.order_id;
      const ck = await api.post(`/intel/orders/${orderId}/checkout`);
      if (ck.data?.checkout_url) {
        window.location.href = ck.data.checkout_url;
        return;
      }
      toast.error("Não foi possível iniciar o checkout");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao criar pedido";
      toast.error(String(msg));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="intel-360-page">
      <TopBar/>
      {/* Hero — imagem 4K institucional (slot: intel_hero) */}
      <section className="relative overflow-hidden min-h-[440px]">
        <ImageSlot slot="intel_hero" alt="Inteligência 360 Hermes" className="absolute inset-0 w-full h-full object-cover"/>
        {/* Gradiente escuro à esquerda para contraste do texto */}
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(11,27,47,0.82) 0%, rgba(11,27,47,0.55) 40%, rgba(11,27,47,0.10) 75%, rgba(11,27,47,0) 100%)` }}/>
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl"
             style={{ background: GOLD }}/>
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center backdrop-blur"
                 style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}77` }}>
              <Sparkles className="w-6 h-6" style={{ color: GOLD }}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD_LIGHT }}>
                Nova vertical Hermes
              </div>
              <h1 className="font-serif text-3xl md:text-5xl text-white" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.4)" }}>
                Inteligência <span style={{ color: GOLD }}>360</span>
              </h1>
            </div>
          </div>
          <p className="text-lg max-w-2xl text-white/90" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
            Dados públicos. Inteligência real. Decisões melhores.
          </p>
          <div className="mt-8 max-w-2xl bg-white/95 rounded-xl shadow-2xl p-2 flex gap-2">
            <div className="flex items-center gap-2 flex-1 px-3 py-2 text-slate-400">
              <Search className="w-5 h-5"/>
              <input disabled
                placeholder="CNPJ, CPF, Nome, Processo, OAB, ANM, Município, Palavra-chave…"
                className="w-full text-sm bg-transparent outline-none placeholder:text-slate-400"
                data-testid="intel-360-search-placeholder"/>
            </div>
            <button disabled title="Busca universal — integração em preparação"
              className="px-5 py-2 rounded-lg text-white text-sm font-semibold cursor-not-allowed opacity-70"
              style={{ background: GOLD }}>Buscar</button>
          </div>
          <p className="text-xs mt-2" style={{ color: `${GOLD_LIGHT}aa` }}>
            <AlertCircle className="w-3 h-3 inline mr-1"/>
            Busca universal será liberada após homologação de todas as fontes.
          </p>
        </div>
      </section>

      {/* KPIs */}
      <section className="w-full px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Fontes catalogadas" value={status?.sources_total ?? "—"} sub="Source Registry"/>
        <KPI label="Credenciais OK" value={status?.sources_credential_ok ?? "—"}
             sub="prontas para ativar" ok={(status?.sources_credential_ok || 0) > 0}/>
        <KPI label="Produtos catalogados" value={status?.products_total ?? "—"} sub="com preço seed"/>
        <KPI label="Produtos ativos" value={status?.products_active ?? "—"}
             sub="disponíveis para compra" ok={(status?.products_active || 0) > 0}/>
      </section>

      {/* Produtos de lançamento */}
      <section className="w-full px-6 lg:px-8 pb-6">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Disponível agora</div>
            <h2 className="font-heading text-2xl font-semibold" style={{ color: NAVY }}>
              Consultas oficiais liberadas
            </h2>
          </div>
          <Link to="/inteligencia/pedidos" data-testid="link-orders"
                className="text-sm underline decoration-dotted" style={{ color: NAVY }}>
            Meus pedidos <ArrowRight className="inline w-3 h-3"/>
          </Link>
          <Link to="/inteligencia/watches" data-testid="link-watches"
                className="text-sm underline decoration-dotted ml-3" style={{ color: NAVY }}>
            Watches <ArrowRight className="inline w-3 h-3"/>
          </Link>
          <Link to="/inteligencia/radar" data-testid="link-radar"
                className="text-sm underline decoration-dotted ml-3 font-semibold" style={{ color: GOLD }}>
            🔴 Radar de Risco <ArrowRight className="inline w-3 h-3"/>
          </Link>
          <Link to="/inteligencia/contratacoes" data-testid="link-contratacoes"
                className="text-sm underline decoration-dotted ml-3" style={{ color: NAVY }}>
            Contratações <ArrowRight className="inline w-3 h-3"/>
          </Link>
          <Link to="/inteligencia/legislativo" data-testid="link-legislativo"
                className="text-sm underline decoration-dotted ml-3" style={{ color: NAVY }}>
            Legislativo <ArrowRight className="inline w-3 h-3"/>
          </Link>
          <Link to="/admin/intelligence" data-testid="link-admin-intel"
                className="text-sm underline decoration-dotted ml-3" style={{ color: NAVY }}>
            Admin <ArrowRight className="inline w-3 h-3"/>
          </Link>
        </div>

        {launchProducts.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Nenhum produto de lançamento ativo. Peça ao Owner rodar
            <code className="mx-1 px-1 rounded bg-white border">POST /api/intel/admin/products/activate-launch</code>.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
            {launchProducts.map((p) => (
              <Intel360ServiceCard
                key={p.slug}
                product={p}
                onBuy={() => openBuy(p.slug)}
                categoryLabel={getCatLabel(p.category)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Catálogo completo · TODOS os serviços agrupados por categoria */}
      <section className="w-full px-6 lg:px-8 py-8 bg-white border-t border-slate-200" data-testid="intel-360-catalog">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline justify-between mb-1">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Catálogo completo</div>
              <h2 className="font-heading text-2xl font-semibold" style={{ color: NAVY }}>
                Todos os serviços Inteligência 360
              </h2>
            </div>
            <div className="text-[11px] text-slate-500">
              {products.length} produto{products.length !== 1 ? "s" : ""} · {catalogByCategory.length} categorias
            </div>
          </div>
          <p className="text-sm text-slate-600 max-w-3xl mb-6">
            Cada serviço só fica <b>ativo</b> após homologação da fonte oficial. Os demais aparecem como
            "integração em preparação" — nenhum resultado é inventado e nenhuma cobrança é feita.
          </p>

          {catalogByCategory.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500 text-center">
              Nenhum produto adicional catalogado. Peça ao Owner rodar
              <code className="mx-1 px-1 rounded bg-white border">POST /api/intel/admin/products/seed</code>.
            </div>
          ) : (
            <div className="space-y-10">
              {catalogByCategory.map(([cat, list]) => {
                const catDoc = catBySlug[cat] || {};
                const label = catDoc.name || cat;
                const color = catDoc.button_color || GOLD;
                const Icon = getLucideIcon(catDoc.icon, Sparkles);
                const activeCount = list.filter(p => p.active).length;
                return (
                  <div key={cat} data-testid={`intel-cat-${cat}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                           style={{ background: `${color}18`, border: `1px solid ${color}55`, color: color }}>
                        <Icon className="w-4 h-4"/>
                      </div>
                      <div>
                        <div className="font-heading text-lg font-semibold" style={{ color: NAVY }}>
                          {label}
                        </div>
                        <div className="text-[10.5px] text-slate-500 uppercase tracking-widest">
                          {list.length} serviço{list.length !== 1 ? "s" : ""} · {activeCount} ativo{activeCount !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
                      {list.map((p) => (
                        <Intel360ServiceCard
                          key={p.slug}
                          product={p}
                          onBuy={() => openBuy(p.slug)}
                          categoryLabel={label}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Últimos pedidos */}
      {orders.length > 0 && (
        <section className="w-full px-6 lg:px-8 pb-6">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Seus últimos pedidos</div>
          <div className="grid md:grid-cols-3 gap-3">
            {orders.slice(0,3).map(o => (
              <Link key={o.order_id}
                    to={`/inteligencia/pedidos?open=${o.order_id}`}
                    data-testid={`order-preview-${o.order_id}`}
                    className="bg-white border rounded-xl p-3 hover:shadow-sm transition block">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{o.product_slug}</div>
                <div className="font-mono text-xs mt-0.5">{o.order_id}</div>
                <div className="text-sm mt-1 text-slate-600">{o.identifier_input}</div>
                <StatusBadge status={o.status}/>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Info bar */}
      <section className="w-full px-6 lg:px-8 pb-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <KeyRound className="w-5 h-5 shrink-0 mt-0.5 text-amber-700"/>
          <div className="text-sm text-amber-900">
            <b>Fase 2 no ar (aditivo, zero regressão):</b> DataJud (CNJ) e CGU Transparência entregam dados oficiais reais
            após pagamento Stripe. Cada consulta é auditada com <code className="bg-white/80 px-1 rounded">source_name</code>
            e <code className="bg-white/80 px-1 rounded">retrieved_at</code>.
            <span className="block mt-1 text-xs text-amber-800">
              Regra Hermes: nada de dado inventado. Se a fonte oficial não responder, exibimos SOURCE_UNAVAILABLE.
            </span>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="w-full px-6 lg:px-8 pb-16">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">
          Roadmap oficial · Cláusula 100 do prompt mestre
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {title:"Fase 0", desc:"Auditoria + Source Registry + Produtos", done:true},
            {title:"Fase 1", desc:"Admin comercial + Franquias + Créditos", done:false},
            {title:"Fase 2", desc:"DataJud + Compliance 360 · comerciais ao vivo", done:true},
            {title:"Fase 3", desc:"CNPJ 360 · Pessoa 360 · Dossiês · Due Diligence", done:false},
            {title:"Fase 4", desc:"Watch Engine (snapshot/diff/alertas)", done:true},
            {title:"Fase 5", desc:"PGFN Dívida Ativa · ingest bulk", done:true},
            {title:"Fase 6", desc:"PNCP + Câmara + Senado + TCU · APIs oficiais", done:true},
            {title:"Fase 7", desc:"IBAMA + SIGMINE + BCB · Radar de Risco 360", done:true},
          ].map(f => (
            <div key={f.title} className={`rounded-xl border p-4 ${f.done ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-1">
                {f.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <div className="w-4 h-4 rounded-full border-2 border-slate-300"/>}
                <div className={`text-xs uppercase tracking-widest font-bold ${f.done ? "text-emerald-700" : "text-slate-400"}`}>{f.title}</div>
              </div>
              <div className="text-sm text-slate-700 leading-snug">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Modal pré-compra */}
      {openSlug && (() => {
        const p = products.find(x => x.slug === openSlug);
        const label = p?.name || "Produto";
        const inputLabel = p?.metadata?.input_label || "Identificador de busca";
        const placeholder = p?.metadata?.placeholder || "CPF, CNPJ, Processo, OAB ou palavra-chave";
        const priceBRL = p ? (p.current_price_cents / 100).toFixed(2).replace(".", ",") : "—";
        return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => !busy && setOpenSlug(null)}
             data-testid="prepay-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
                <Zap className="w-5 h-5" style={{ color: GOLD }}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Pré-compra</div>
                <div className="font-heading text-lg font-semibold truncate" style={{ color: NAVY }}>
                  {label}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Total</div>
                <div className="text-xl font-heading font-semibold" style={{ color: NAVY }}>
                  R$ {priceBRL}
                </div>
              </div>
            </div>
            <label className="text-xs uppercase tracking-widest text-slate-500">
              {inputLabel}
            </label>
            <input autoFocus value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={placeholder}
              disabled={busy}
              data-testid="prepay-identifier-input"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-slate-500"/>
            <div className="mt-3 text-xs text-slate-500 leading-relaxed">
              <AlertCircle className="w-3 h-3 inline mr-1"/>
              Após o pagamento no Stripe, o relatório é gerado com dados oficiais e fica em
              <b> Meus Pedidos</b>. Nenhum dado é exibido antes da confirmação do pagamento.
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => !busy && setOpenSlug(null)}
                      className="flex-1 py-2.5 rounded-lg border border-slate-300 text-sm font-medium"
                      disabled={busy}>Cancelar</button>
              <button onClick={submitBuy}
                      disabled={busy || !identifier.trim()}
                      data-testid="prepay-submit"
                      className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                      style={{ background: NAVY }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ExternalLink className="w-4 h-4"/>}
                Ir para Stripe
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function KPI({ label, value, sub, ok }) {
  return (
    <div className={`rounded-xl border p-3 bg-white ${ok ? "border-emerald-300" : "border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-heading font-semibold" style={{ color: NAVY }}>{value}</div>
      <div className="text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    ready:   { c: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "PRONTO" },
    delivered: { c: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "ENTREGUE" },
    processing: { c: "bg-blue-50 text-blue-700 border-blue-300", label: "PROCESSANDO" },
    paid: { c: "bg-blue-50 text-blue-700 border-blue-300", label: "PAGO" },
    awaiting_payment: { c: "bg-amber-50 text-amber-700 border-amber-300", label: "AGUARDANDO PGTO" },
    draft: { c: "bg-slate-50 text-slate-500 border-slate-300", label: "RASCUNHO" },
    failed: { c: "bg-rose-50 text-rose-700 border-rose-300", label: "FALHOU" },
    refunded: { c: "bg-slate-50 text-slate-500 border-slate-300", label: "REEMBOLSADO" },
  };
  const s = map[status] || { c: "bg-slate-50 text-slate-500 border-slate-300", label: (status || "").toUpperCase() };
  return (
    <span className={`inline-block mt-2 text-[10px] tracking-widest border rounded px-1.5 py-0.5 ${s.c}`}>
      {s.label}
    </span>
  );
}

